import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CustomNotification } from 'common/components';

import * as productServices from 'services/product';
import { forwardTo } from 'utils/common/route';

export const productFullViewKeys = {
  all: ['product-full-view'],
  itemData: (productId) => [...productFullViewKeys.all, parseInt(productId)],
};

export const productOriginFullViewKeys = {
  all: ['pim/overlays', 'get-product-original-details-full-view'],
  itemData: (productId) => [
    ...productOriginFullViewKeys.all,
    { params: { productItemId: parseInt(productId) } },
  ],
};

const defaultProductFull = {};

export const useGetProductFullView = ({ productId, ...otherOptions } = {}) => {
  const queryClient = useQueryClient();

  const queries = useQuery({
    queryKey: productFullViewKeys.itemData(productId),
    queryFn: async () => {
      const response = await productServices.getProductFullView({
        productItemId: productId,
      });

      if (response?.isSuccess) {
        return response?.data;
      }
      CustomNotification.error(response?.message ?? 'Something went wrong');
      forwardTo('/products');
    },
    enabled: Boolean(productId),
    retry: 1,
    ...otherOptions,
  });

  const handleRefetchProductFullView = () => {
    queryClient.invalidateQueries({
      queryKey: productFullViewKeys.itemData(productId),
    });
  };

  const invalidateAllProductFull = () => {
    queryClient.invalidateQueries({
      queryKey: productFullViewKeys.all,
    });
  };

  return {
    productFull: queries?.data ?? defaultProductFull,
    handleRefetchProductFullView,
    invalidateAllProductFull,
    ...queries,
  };
};

export const useGetProductOriginFullViewForProductOverlay = ({
  productOverlayId,
  ...otherOptions
} = {}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: productOriginFullViewKeys.itemData(productOverlayId),
    staleTime: 30 * 1000,
    enabled: Boolean(productOverlayId),
    ...otherOptions,
  });

  const invalidAllProductOriginFull = () => {
    queryClient.invalidateQueries({
      queryKey: productOriginFullViewKeys.all,
    });
  };

  return {
    ...query,
    invalidAllProductOriginFull,
  };
};
